/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import { translate } from 'react-i18next';

import AutoCompleteModel from 'models/forms/AutoCompleteModel.js';

import ElectroTextField from 'forms/components/inputs/ElectroTextField/ElectroTextField';
import ElectroSelectExtra from 'forms/components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroDatePicker from 'forms/components/inputs/ElectroDatePicker/ElectroDatePicker';
import ElectroCheckbox from 'forms/components/inputs/ElectroCheckbox/ElectroCheckbox';
import FormControls from 'forms/components/controls/FormControls/FormControls';
import AddOrRemoveButtonControl from 'forms/components/controls/FormControls/AddOrRemoveButtonControl';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import FormSection from 'components/FormSection/FormSection';
import AssetDetailSection from './components/AssetDetailSection';
import {
  customerDetailsActionList,
  deliveryDetailsActionList,
  genericActionList,
} from './utilities/actionDrawerActions';

import {
  autoCompleteAssetRequestAccountCompanies,
  autoCompleteAssetRequestAccountCostCenter,
  autoCompleteAssetRequestAccountProject,
  autoCompleteAssetRequestCompanySites,
  autoCompleteAssetRequestDefaultAddress,
  autoCompleteManufacturersForAccount,
  autoCompleteTradingAccountsForRequestAssetForm,
} from 'api/forms.js';

import { createAssetRequestForm, makeFields } from './setUpAssetRequestForm.js';
import ElectroResponseMessage from '../components/inputs/ElectroResponseMessage';
import ActionDrawerWrapper from '../../components/ActionDrawerWrapper/ActionDrawerWrapper';
import {
  AddNewCompany,
  AddNewSite,
  AddNewCostCenter,
  AddNewProject,
} from '../../views/ActionDrawer';

import styles from './AssetRequestForm.module.scss';

@translate()
@inject('actionDrawerStore', 'userStore')
@observer
class _AssetRequestForm extends React.Component<*> {
  @observable accountsModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteTradingAccountsForRequestAssetForm
  );

  @observable manufacturerModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteManufacturersForAccount
  );

  @observable projectsModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestAccountProject
  );

  @observable costCentreModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestAccountCostCenter
  );

  @observable customerDetailsCompanyModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestAccountCompanies('customer')
  );

  @observable customerDetailsSiteModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestCompanySites('customer')
  );

  @observable defaultLocationModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestDefaultAddress
  );

  @observable deliveryDetailsCompanyModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestAccountCompanies('delivery')
  );

  @observable deliveryDetailsSiteModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestCompanySites('delivery')
  );

  @observable localState = {
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
    isDeliveryEqual: false,
    hasDefaultAddress: false,
    customerDetailsAddress: '',
    deliveryDetailsAddress: '',
  };

  @computed get form(): Object {
    const { labels, fields, rules, extra } = makeFields(this.props.t);

    return createAssetRequestForm(
      {
        labels,
        fields,
        rules,
        extra,
      },
      this.localState,
      this.props.t
    );
  }

  /**
   * Component did mount hook
   */
  componentDidMount(): void {
    this.form.$('assetDetails').add({
      manufacturer: '',
      model: '',
      quantity: 0,
    });
  }

  /**
   * Renders our trading account section
   * @param form
   * @returns {*}
   */
  renderTradingAccountSection(form): React.Element<*> {
    const accountsModel: Object = this.accountsModel;
    const manufacturerModel: Object = this.manufacturerModel;
    const projectsModel: Object = this.projectsModel;
    const costCentreModel: Object = this.costCentreModel;

    return (
      <FormSection form={form} section="chooseAccount">
        <ElectroSelectExtra
          field={form.$('chooseAccount.tradingAccount')}
          form={form}
          formName="chooseAccount"
          labelKey="name"
          handleOnChange={this.onAccountChange.bind(this)}
          props={{
            model: accountsModel,
            modelChild: [
              manufacturerModel,
              projectsModel,
              costCentreModel,
              this.customerDetailsCompanyModel,
              this.deliveryDetailsCompanyModel,
              this.defaultLocationModel,
            ],
          }}
        />
      </FormSection>
    );
  }

  /**
   * Sets our default address
   * @param account
   * @returns {Promise<void>}
   */
  async onAccountChange(account) {
    this.setFieldDefaults();

    const location = await autoCompleteAssetRequestDefaultAddress('', account.id);
    const hasDefaultAddress = location.active.length > 0;

    if (hasDefaultAddress) {
      const defaultLocation = location.active[0];
      this.localState.hasDefaultAddress = true;
      this.localState.customerDetailsAddress = this.generateAddress('customerDetails')(
        defaultLocation
      );
      this.form.$('customerDetails.company').set('value', defaultLocation.company_name);
      this.form.$('customerDetails.site').set('value', defaultLocation.site_name);
      this.form.$('customerDetails.site').set('extra', {
        selectID: defaultLocation.id,
      });
    } else {
      this.localState.hasDefaultAddress = false;
    }
  }

  /**
   * Setting all necessary defaults
   */
  setFieldDefaults() {
    const currentUser = this.props.userStore.activeUser;

    this.form.$('customerDetails.name').set('value', currentUser.full_name);
    this.form.$('customerDetails.phoneNo').set('value', currentUser.tel);
  }

  /**
   * Renders our asset details section
   *
   * @param form
   * @param index
   * @returns {*}
   */
  renderAssetDetailsSection(form, index = 0): React.Element<*> {
    return (
      <FormSection form={form} section="assetDetails" fluid={true}>
        {this.renderAssetDetailPartial(form)}
        <AddOrRemoveButtonControl
          form={form}
          field={form.$('assetDetails')}
          controls={{
            onAdd: true,
          }}
        />
      </FormSection>
    );
  }

  /**
   * Render asset detail section
   * @param form
   */
  renderAssetDetailPartial(form): React.Element<*> {
    const manufacturerModel: Object = this.manufacturerModel;
    const assets: Array = form.$('assetDetails');

    return assets.map((field, index) => (
      <AssetDetailSection
        form={form}
        field={field}
        key={index}
        index={index}
        manufacturerModel={manufacturerModel}
      />
    ));
  }

  /**
   * Renders required dates section
   * @param form
   * @returns {*}
   */
  renderRequiredDatesSection(form: Object): React.Element<*> {
    return (
      <FormSection form={form} section="requiredDates">
        <ElectroDatePicker field={form.$('requiredDates.startDate')} />
        <ElectroDatePicker field={form.$('requiredDates.endDate')} />
      </FormSection>
    );
  }

  /**
   * Renders additional information section
   * @param form
   * @returns {*}
   */
  renderAdditionalInformationSection(form: Object): React.Element<*> {
    return (
      <FormSection form={form} section="additionalInformation">
        <ElectroTextField
          field={form.$('additionalInformation.details')}
          props={{
            multiline: true,
            rows: 5,
          }}
        />
      </FormSection>
    );
  }

  /**
   * Renders customer details section
   * @param form
   * @returns {*}
   */
  renderCustomerDetailsSection(form: Object): React.Element<*> {
    return (
      <FormSection form={form} section={'customerDetails'}>
        <ElectroTextField field={form.$('customerDetails.name')} />
        <ElectroTextField field={form.$('customerDetails.phoneNo')} />

        {this.renderCustomerDetailsDropdownPartial(form)}

        <div className={styles['address-row']}>{this.localState.customerDetailsAddress}</div>
        <ElectroCheckbox
          field={form.$('customerDetails.deliveryIsEqual')}
          onChange={this.onCheckboxChangeHook.bind(this, form)}
        />
      </FormSection>
    );
  }

  /**
   * Render customer details dropdown partial
   * @param form
   */
  renderCustomerDetailsDropdownPartial(form): React.Element<*> {
    const actionsListData = customerDetailsActionList;
    const { hasDefaultAddress } = this.localState;
    const companyModel = this.customerDetailsCompanyModel;
    const siteModel = this.customerDetailsSiteModel;

    return (
      <React.Fragment>
        {this.localState.hasDefaultAddress ? (
          <React.Fragment>
            <ElectroTextField field={form.$('customerDetails.company')} disabled={true} />
            <ElectroTextField field={form.$('customerDetails.site')} disabled={true} />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <ElectroSelectExtra
              field={form.$('customerDetails.company')}
              form={form}
              formName={'customerDetails'}
              labelKey="name"
              actionButtonData={{
                data: actionsListData[0],
                disabled:
                  hasDefaultAddress || !form.$('chooseAccount.tradingAccount').extra.selectID,
              }}
              props={{
                model: companyModel,
                modelChild: siteModel,
              }}
            />
            <ElectroSelectExtra
              field={form.$('customerDetails.site')}
              form={form}
              formName="site"
              labelKey="name"
              handleOnChange={this.handleSiteChangeHook('customerDetails')}
              actionButtonData={{
                data: actionsListData[1],
                disabled: hasDefaultAddress || !form.$('customerDetails.company').extra.selectID,
              }}
              props={{
                model: siteModel,
              }}
            />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  /**
   * Renders delivery details section
   * @param form
   * @returns {*}
   */
  renderDeliveryDetailsSection(form: Object): React.Element<*> {
    return (
      <FormSection form={form} section={'deliveryDetails'}>
        <ElectroTextField field={form.$('deliveryDetails.name')} />
        <ElectroTextField field={form.$('deliveryDetails.phoneNo')} />

        {this.renderDeliveryDetailsDropdownPartial(form)}

        <div className={styles['address-row']}>{this.localState.deliveryDetailsAddress}</div>
        <ElectroTextField
          field={form.$('deliveryDetails.specialInstructions')}
          props={{
            multiline: true,
            rows: 5,
          }}
        />
      </FormSection>
    );
  }

  /**
   * Render delivery details dropdown partial
   * @param form
   */
  renderDeliveryDetailsDropdownPartial(form): React.Element<*> {
    const actionsListData = deliveryDetailsActionList;
    const companyModel = this.deliveryDetailsCompanyModel;
    const siteModel = this.deliveryDetailsSiteModel;

    return (
      <React.Fragment>
        {this.localState.isDeliveryEqual && (
          <React.Fragment>
            <ElectroTextField field={form.$('deliveryDetails.company')} disabled={true} />
            <ElectroTextField field={form.$('deliveryDetails.site')} disabled={true} />
          </React.Fragment>
        )}

        {!this.localState.isDeliveryEqual && (
          <React.Fragment>
            <ElectroSelectExtra
              field={form.$('deliveryDetails.company')}
              form={form}
              formName={'deliveryDetails'}
              labelKey="name"
              actionButtonData={{
                data: actionsListData[0],
                disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
              }}
              props={{
                model: companyModel,
                modelChild: siteModel,
              }}
            />
            <ElectroSelectExtra
              field={form.$('deliveryDetails.site')}
              form={form}
              formName="site"
              labelKey="name"
              handleOnChange={this.handleSiteChangeHook('deliveryDetails')}
              actionButtonData={{
                data: actionsListData[1],
                disabled: !form.$('deliveryDetails.company').extra.selectID,
              }}
              props={{
                model: siteModel,
              }}
            />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  /**
   * Handles our select onchange hook
   * @param type
   */
  handleSiteChangeHook(type: string) {
    return this.generateAddress(type);
  }

  /**
   * Generating address string
   * @param type
   * @returns {function(Object): void}
   */
  generateAddress(type: string) {
    return (location: Object) => {
      const { address } = location;
      let fullAddress = [];

      for (let key in address) {
        if (key.includes('address') && address[key]) {
          fullAddress.push(address[key]);
        }
      }

      fullAddress.push(address.postcode);

      // this.localState.customerDetailsAddress || this.localState.deliveryDetailsAddress
      this.localState[`${type}Address`] = fullAddress.join(', ');

      return fullAddress.join(', ');
    };
  }

  /**
   * Sets our checkbox value
   * @param form
   * @param field
   */
  async onCheckboxChangeHook(form: Object, field: Object): void {
    const values = form.values();
    const { customerDetails } = values;

    this.localState.isDeliveryEqual = field.value;

    if (field.value) {
      form.$('deliveryDetails.name').set('value', customerDetails.name);
      form.$('deliveryDetails.phoneNo').set('value', customerDetails.phoneNo);
      form.$('deliveryDetails.company').set('value', customerDetails.company);
      form.$('deliveryDetails.site').set('value', customerDetails.site);
      this.localState.deliveryDetailsAddress = this.localState.customerDetailsAddress;
    } else {
      form.$('deliveryDetails.name').set('value', '');
      form.$('deliveryDetails.phoneNo').set('value', '');
      form.$('deliveryDetails.company').set('value', '');
      form.$('deliveryDetails.site').set('value', '');
      this.localState.deliveryDetailsAddress = null;
    }
  }

  /**
   * Renders our action drawer
   * @param form
   * @param formName
   * @param fields
   * @returns {*}
   */
  getActionDrawerData(form: Object, formName: string, fields: Object): React.Element<*> {
    const type = formName.split('Details')[0];

    return {
      [`${formName}NewCompany`]: (
        <AddNewCompany
          parentForm={form}
          fieldList={fields}
          accountsModel={this.accountsModel}
          model={this[`${formName}CompanyModel`]}
          isAssetRequest={true}
          locationType={type}
        />
      ),
      [`${formName}NewSite`]: (
        <AddNewSite
          parentForm={form}
          fieldList={fields}
          accountsModel={this.accountsModel}
          companyModel={this[`${formName}CompanyModel`]}
          model={this[`${formName}SiteModel`]}
          isAssetRequest={true}
          locationType={type}
        />
      ),
    };
  }

  /**
   * Renders assign projects section
   * @param form
   * @returns {*}
   */
  renderAssignProjectsSection(form: Object): React.Element<*> {
    const projectsModel: Object = this.projectsModel;

    return (
      <FormSection form={form} section="assignProjects">
        <ElectroSelectExtra
          field={form.$('assignProjects.list')}
          form={form}
          formName="assignProjects"
          labelKey={['code', 'name']}
          labelSeparator=" - "
          actionButtonData={{
            data: genericActionList[0],
            disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
          }}
          props={{
            model: projectsModel,
          }}
        />
      </FormSection>
    );
  }

  /**
   * Renders assign cost centre section
   * @param form
   * @returns {*}
   */
  renderAssignCostCentreSection(form: Object): React.Element<*> {
    const costCentreModel: Object = this.costCentreModel;

    return (
      <FormSection form={form} section="assignCostCentre">
        <ElectroSelectExtra
          field={form.$('assignCostCentre.list')}
          form={form}
          formName="assignCostCentre"
          labelKey={['code', 'name']}
          labelSeparator=" - "
          actionButtonData={{
            data: genericActionList[1],
            disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
          }}
          props={{
            model: costCentreModel,
          }}
        />
      </FormSection>
    );
  }

  /**
   * Render form controls section
   * @param form
   * @returns {*}
   */
  renderFormControlsSection(form: Object): React.Element<*> {
    return <FormControls buttonText={'Submit Asset Request'} form={form} />;
  }

  /**
   * render function
   * TODO: i18n
   */
  render(): React.Element<*> {
    const form: Object = this.form;
    const currentUser: Object = this.props.userStore.getActiveUserFromLocal();

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <ElectroResponseMessage localState={this.localState} />
          {/*TODO: i18n on title*/}
          <FormWrapper form={form} title={'New Asset Request'}>
            {this.renderTradingAccountSection(form)}
            {this.renderAssetDetailsSection(form)}
            {this.renderRequiredDatesSection(form)}
            {this.renderAdditionalInformationSection(form)}
            {this.renderCustomerDetailsSection(form)}
            {this.renderDeliveryDetailsSection(form)}
            {this.renderAssignProjectsSection(form)}
            {this.renderAssignCostCentreSection(form)}
            {this.renderFormControlsSection(form)}
          </FormWrapper>
        </PageWrapper>
        <ActionDrawerWrapper
          closeBackdropOnClick={false}
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              ...this.getActionDrawerData(form, 'customerDetails', {
                account: 'chooseAccount.tradingAccount',
                company: 'customerDetails.company',
                site: 'customerDetails.site',
              }),
              ...this.getActionDrawerData(form, 'deliveryDetails', {
                account: 'chooseAccount.tradingAccount',
                company: 'deliveryDetails.company',
                site: 'deliveryDetails.site',
              }),
              newProject: (
                <AddNewProject
                  parentForm={form}
                  accountsModel={this.accountsModel}
                  model={this.projectsModel}
                  localState={this.localState}
                  fieldList={{
                    account: 'chooseAccount.tradingAccount',
                    project: 'assignProjects.list',
                  }}
                  hideSections={['location']}
                  prefillFields={{
                    'addNewProject.projectStartDate': form.$('requiredDates.startDate'),
                    'addNewProject.projectEndDate': form.$('requiredDates.endDate'),
                    'addNewProject.chooseLead': {
                      value: `${currentUser.first_name} ${currentUser.last_name}`,
                      extra: {
                        selectID: currentUser.id,
                      },
                    },
                    'addNewProject.projectEngineer': {
                      value: currentUser.id,
                      extra: {
                        selectID: currentUser.id,
                      },
                    },
                    'location.locationCompany': this.localState.isDeliveryEqual
                      ? form.$('customerDetails.company')
                      : form.$('deliveryDetails.company'),
                    'location.locationSite': this.localState.isDeliveryEqual
                      ? form.$('customerDetails.site')
                      : form.$('deliveryDetails.site'),
                  }}
                  replaceModels={{
                    'location.locationCompany': this.localState.isDeliveryEqual
                      ? this.customerDetailsCompanyModel
                      : this.deliveryDetailsCompanyModel,
                    'location.locationSite': this.localState.isDeliveryEqual
                      ? this.customerDetailsSiteModel
                      : this.deliveryDetailsSiteModel,
                  }}
                />
              ),
              newCostCenter: (
                <AddNewCostCenter
                  parentForm={form}
                  accountsModel={this.accountsModel}
                  model={this.costCentreModel}
                  localState={this.localState}
                  fieldList={{
                    account: 'chooseAccount.tradingAccount',
                    costCentre: 'assignCostCentre.list',
                  }}
                />
              ),
            }[this.props.actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}

export const AssetRequestForm = _AssetRequestForm;
